import {
    createVendorLane,
    updateVendorLane,
    type VendorLaneInput,
} from '@api/vendors';
import { listWarehousesForPicker } from '@api/work-order';
import {
    Button,
    Group,
    Modal,
    NumberInput,
    Select,
    Stack,
    TextInput,
} from '@mantine/core';
import React, { useEffect, useState } from 'react';

import { type LaneRow } from './laneColumns';

/**
 * The lane editor, shared by the two screens that show a vendor's lanes: the Suppliers
 * page's vendor pane, and Settings → Lanes.
 *
 * Lifted here rather than duplicated, because a lane row and the modal that edits it are
 * the same thing in both places — the only difference is which screen picked the vendor.
 * Both read and write through the same three vendor-lane endpoints; there is no
 * "all lanes" route and this deliberately does not need one.
 *
 * ADD AND EDIT ONLY. There is no DELETE route on vendor lanes and none is wanted yet: a
 * lane feeds every expected date on a shipment, so removing one silently changes dates on
 * work already in flight. Correcting a wrong lane is an edit.
 */

/**
 * Edits one of a vendor's lanes, or adds one. Lead times drive every expected date on a
 * shipment and the container cap drives the CBM estimate and warning; before this existed
 * both were database-only.
 */
export const LaneEditModal: React.FC<{
    opened: boolean;
    vendorId: string | null;
    lane: LaneRow | null;
    cbmEnabled: boolean;
    onClose: () => void;
    onSaved: () => void;
}> = ({ opened, vendorId, lane, cbmEnabled, onClose, onSaved }) => {
    const [warehouses, setWarehouses] = useState<
        Array<{ warehouseId: string; name: string }>
    >([]);
    const [form, setForm] = useState<VendorLaneInput>({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!opened) return;
        setError(null);
        setForm(
            lane
                ? {
                      warehouseId: lane.warehouseId,
                      source: lane.source === '—' ? null : lane.source,
                      destination:
                          lane.destination === '—' ? null : lane.destination,
                      mode: lane.mode,
                      productionTime: lane.productionTime,
                      qcTime: lane.qcTime,
                      transitTime: lane.transitTime,
                      deliveryTime: lane.deliveryTime,
                      maxCbmPerShipment: lane.maxCbmPerShipment,
                      minFillPct: lane.minFillPct ?? null,
                  }
                : {},
        );
        listWarehousesForPicker()
            .then((rows) =>
                setWarehouses(
                    rows.map((w) => ({
                        warehouseId: w.warehouseId,
                        name: w.name || w.warehouseId,
                    })),
                ),
            )
            .catch(() => setWarehouses([]));
    }, [opened, lane]);

    const setDays =
        (key: 'productionTime' | 'qcTime' | 'transitTime' | 'deliveryTime') =>
        (v: number | string) =>
            setForm((f) => ({ ...f, [key]: typeof v === 'number' ? v : null }));

    const save = async () => {
        if (!vendorId) return;
        if (!lane && !form.warehouseId) {
            setError('Pick a destination warehouse');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            if (lane?.laneId) await updateVendorLane(vendorId, lane.laneId, form);
            else await createVendorLane(vendorId, form);
            onSaved();
            onClose();
        } catch (e) {
            setError(
                (e as { message?: string })?.message ?? 'Could not save the lane',
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={lane ? 'Edit lane' : 'Add lane'}
            zIndex={9999}
            size="md"
        >
            <Stack gap={10}>
                <Select
                    label="Destination warehouse"
                    data={warehouses.map((w) => ({
                        value: w.warehouseId,
                        label: w.name,
                    }))}
                    value={form.warehouseId ?? null}
                    onChange={(v) =>
                        setForm((f) => ({ ...f, warehouseId: v ?? undefined }))
                    }
                    searchable
                    required={!lane}
                />
                <Group grow>
                    <TextInput
                        label="Port of loading"
                        value={form.source ?? ''}
                        onChange={(e) =>
                            setForm((f) => ({
                                ...f,
                                source: e.currentTarget.value || null,
                            }))
                        }
                    />
                    <TextInput
                        label="Port of unloading"
                        value={form.destination ?? ''}
                        onChange={(e) =>
                            setForm((f) => ({
                                ...f,
                                destination: e.currentTarget.value || null,
                            }))
                        }
                    />
                </Group>
                <TextInput
                    label="Mode"
                    placeholder="Sea, Road, Air, Rail"
                    value={form.mode ?? ''}
                    onChange={(e) =>
                        setForm((f) => ({
                            ...f,
                            mode: e.currentTarget.value || null,
                        }))
                    }
                />
                <Group grow>
                    <NumberInput
                        label="Production (days)"
                        value={form.productionTime ?? ''}
                        onChange={setDays('productionTime')}
                        min={0}
                        hideControls
                    />
                    <NumberInput
                        label="QC (days)"
                        value={form.qcTime ?? ''}
                        onChange={setDays('qcTime')}
                        min={0}
                        hideControls
                    />
                </Group>
                <Group grow>
                    <NumberInput
                        label="Transit (days)"
                        value={form.transitTime ?? ''}
                        onChange={setDays('transitTime')}
                        min={0}
                        hideControls
                    />
                    <NumberInput
                        label="Delivery (days)"
                        value={form.deliveryTime ?? ''}
                        onChange={setDays('deliveryTime')}
                        min={0}
                        hideControls
                    />
                </Group>
                {cbmEnabled && (
                    <NumberInput
                        label="Max CBM per shipment"
                        description="The container cap for this leg. Blank means no cap: totals show, nothing is estimated or warned."
                        value={form.maxCbmPerShipment ?? ''}
                        onChange={(v) =>
                            setForm((f) => ({
                                ...f,
                                maxCbmPerShipment:
                                    typeof v === 'number' ? v : null,
                            }))
                        }
                        min={0}
                        hideControls
                    />
                )}
                {cbmEnabled && (
                    <NumberInput
                        label="Minimum fill (% of the cap)"
                        description="Below this, a suggested load is flagged as under-filled. A nudge, never a block. Blank means no opinion."
                        value={form.minFillPct ?? ''}
                        onChange={(v) =>
                            setForm((f) => ({
                                ...f,
                                minFillPct: typeof v === 'number' ? v : null,
                            }))
                        }
                        min={0}
                        max={100}
                        hideControls
                    />
                )}
                {error && (
                    <span style={{ fontSize: 12, color: '#E53E3E' }}>{error}</span>
                )}
                <Group justify="flex-end" mt={4}>
                    <Button
                        variant="default"
                        size="xs"
                        onClick={onClose}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="xs"
                        onClick={() => void save()}
                        loading={saving}
                    >
                        {lane ? 'Save lane' : 'Add lane'}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};
